/*
Hook for real-time unread message count using SSE
Updates every 30 seconds from the server

El contador de "Mensajes" suma consultas + otros. Antes sólo contaba consultas
(inquiry: true), así que los leads de WhatsApp de MercadoLibre y los "Mostrar
teléfono" de Yapo —que caen en la pestaña Otros— nunca movían el badge ni
disparaban el toast. El stakeholder pidió que el badge refleje toda la bandeja
de entrada, y que el toast de "nuevo mensaje" siga al mismo número.

Author: AI Assistant
Date: 2025-01-27
*/
import { useState, useEffect, useRef } from 'react';

interface UnreadCounts {
  consultas: number;
  otros?: number; // Future extension
  deals?: number; // Active deals count
}

interface UseUnreadMessageCountReturn {
  /** consultas + otros: lo que muestra el badge de "Mensajes" */
  messagesCount: number;
  consultasCount: number;
  otrosCount: number;
  dealsCount: number;
  isConnected: boolean;
  isLoading: boolean;
  error: string | null;
  reconnect: () => void;
}

export const useUnreadMessageCount = (
  organizationId: string | null,
  enabled: boolean = true,
  onCountIncrease?: (newCount: number, previousCount: number) => void,
  onDealCountIncrease?: (newCount: number, previousCount: number) => void
): UseUnreadMessageCountReturn => {
  const [messagesCount, setMessagesCount] = useState(0);
  const [consultasCount, setConsultasCount] = useState(0);
  const [otrosCount, setOtrosCount] = useState(0);
  const [dealsCount, setDealsCount] = useState(0);
  const [isConnected, setIsConnected] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const eventSourceRef = useRef<EventSource | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const previousMessagesCountRef = useRef<number>(0);
  const previousDealsCountRef = useRef<number>(0);

  const cleanup = () => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
  };

  const connect = () => {
    console.log('🔍 SSE Connect attempt:', { organizationId, enabled, hasOrgId: !!organizationId });
    
    if (!organizationId || !enabled) {
      console.log('❌ SSE Connect: Skipped', { organizationId, enabled });
      setIsLoading(false);
      return;
    }

    // Clean up any existing connection
    cleanup();

    try {
      setError(null);
      setIsLoading(true);

      // Get auth token from localStorage for SSE authentication
      const authToken = localStorage.getItem('authToken');
      if (!authToken) {
        setError('No authentication token available');
        setIsLoading(false);
        return;
      }

      const eventSource = new EventSource(
        `${import.meta.env.VITE_API_URL}messages/sse/unread-count/${organizationId}?token=${encodeURIComponent(authToken)}`,
        { withCredentials: true }
      );

      eventSource.onopen = () => {
        console.log('SSE connection opened for unread message count');
        setIsConnected(true);
        setIsLoading(false);
        setError(null);
      };

      eventSource.onmessage = (event) => {
        try {
          const data: UnreadCounts = JSON.parse(event.data);
          console.log('📊 SSE Data received:', data);

          const newConsultasCount = data.consultas || 0;
          const newOtrosCount = data.otros || 0;
          const newMessagesCount = newConsultasCount + newOtrosCount;
          const newDealsCount = data.deals || 0;
          const previousMessagesCount = previousMessagesCountRef.current;
          const previousDealsCount = previousDealsCountRef.current;
          
          console.log('📊 Counts update:', {
            consultas: newConsultasCount,
            otros: newOtrosCount,
            mensajes: newMessagesCount,
            deals: newDealsCount
          });
          
          // Detect message count increase (consultas + otros) and trigger callback
          if (onCountIncrease && newMessagesCount > previousMessagesCount) {
            console.log('📈 Message count increased:', {
              previous: previousMessagesCount,
              new: newMessagesCount,
              increment: newMessagesCount - previousMessagesCount
            });
            onCountIncrease(newMessagesCount, previousMessagesCount);
          }
          
          // Detect deal count increase and trigger callback
          if (onDealCountIncrease && newDealsCount > previousDealsCount) {
            console.log('🤝 Deal count increased:', {
              previous: previousDealsCount,
              new: newDealsCount,
              increment: newDealsCount - previousDealsCount
            });
            onDealCountIncrease(newDealsCount, previousDealsCount);
          }
          
          // Update state and refs
          setMessagesCount(newMessagesCount);
          setConsultasCount(newConsultasCount);
          setOtrosCount(newOtrosCount);
          setDealsCount(newDealsCount);
          previousMessagesCountRef.current = newMessagesCount;
          previousDealsCountRef.current = newDealsCount;
          setError(null);
        } catch (parseError) {
          setError('Error parsing server response');
        }
      };

      eventSource.onerror = (errorEvent) => {
        console.error('SSE connection error:', errorEvent);
        setIsConnected(false);
        setIsLoading(false);
        setError('Connection error');

        // Auto-reconnect after 5 seconds
        reconnectTimeoutRef.current = setTimeout(() => {
          console.log('Attempting to reconnect SSE for unread message count...');
          connect();
        }, 5000);
      };

      // Handle custom error events from server
      eventSource.addEventListener('error', (event: any) => {
        try {
          const errorData = JSON.parse(event.data);
          setError(errorData.error || 'Server error');
        } catch {
          setError('Server error');
        }
      });

      eventSourceRef.current = eventSource;
    } catch (connectionError) {
      console.error('Failed to create SSE connection:', connectionError);
      setError('Failed to connect');
      setIsLoading(false);
      setIsConnected(false);
    }
  };

  const reconnect = () => {
    connect();
  };

  useEffect(() => {
    // Always cleanup existing connection when organization changes
    cleanup();
    
    if (organizationId && enabled) {
      // Small delay to ensure cleanup is complete
      setTimeout(() => {
        connect();
      }, 100);
    }

    // Cleanup on unmount or when dependencies change
    return cleanup;
  }, [organizationId, enabled]);

  // Cleanup on unmount
  useEffect(() => {
    return cleanup;
  }, []);

  return {
    messagesCount,
    consultasCount,
    otrosCount,
    dealsCount,
    isConnected,
    isLoading,
    error,
    reconnect
  };
};